import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, IconButton, Typography, TextField, CircularProgress, Divider, Button, Chip, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useDispatch, useSelector } from "react-redux";
import default_theme from "assets/themes/themes";
import { openPdfChat, closeChat, fetchHistoryRequest, sendMessageRequest } from "../../redux/agents/agentsSlice";

const PDFChatPopup = ({ projectId, documentIds }) => {
  const dispatch = useDispatch();
  const [input, setInput] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const messagesEndRef = useRef(null);

  const { isOpen, mode, messages, loading, historyLoading } = useSelector((state) => state.agents);
  const isPdfMode = isOpen && mode === "pdf";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, isPdfMode]);

  useEffect(() => {
    if (isPdfMode && projectId) {
      dispatch(fetchHistoryRequest({ projectId, mode: "pdf" }));
    }
  }, [isPdfMode, projectId, dispatch]);

  useEffect(() => {
    let interval;
    
    if (loading) {
      const phrases = [
        "Thinking...",
        "Reading the document...",
        "Analyzing pages...",
        "Extracting context...",
        "Refining answer...",
        "Almost there..."
      ];
      
      let currentIndex = 0;
      setLoadingText(phrases[0]);
      
      // Change the text every 12 seconds in a loop
      interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % phrases.length;
        setLoadingText(phrases[currentIndex]);
      }, 12000);
      
    } else {
      setLoadingText("");
    }
    
    return () => clearInterval(interval);
  }, [loading]);

  const handleSend = () => {
    if (!input.trim() || loading) return;
    dispatch(sendMessageRequest({ projectId, message: input.trim(), mode: "pdf", documentIds }));
    setInput("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
      {/* Trigger Button */}
      <Button
        variant="outlined"
        startIcon={<ChatBubbleOutlineIcon />}
        onClick={() => dispatch(openPdfChat())}
        sx={{
          color: default_theme.palette.other.figmaBlue,
          borderColor: default_theme.palette.other.figmaBlue,
          "&:hover": { backgroundColor: "#f0f7ff" },
        }}
      >
        AI Document Assistant
      </Button>

      {/* Chat Popup */}
      {isPdfMode && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed", bottom: 24, right: 24, width: 400, height: 600,
            display: "flex", flexDirection: "column", borderRadius: 3, overflow: "hidden", zIndex: 1300,
          }}
        >
          <Box sx={{ p: 2, backgroundColor: default_theme.palette.other.figmaBlue, color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SmartToyIcon />
              <Typography variant="subtitle1" fontWeight={600}>Document Assistant</Typography>
            </Box>
            <IconButton size="small" onClick={() => dispatch(closeChat())} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, p: 2, overflowY: "auto", backgroundColor: "#F8FAFC" }}>
            {historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress size={24} /></Box>
            ) : messages.length === 0 ? (
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 4 }}>
                Ask me anything about the handbook document!
              </Typography>
            ) : (
              messages.map((msg, index) => (
                <Box key={index} sx={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", mb: 2 }}>
                  <Box sx={{ maxWidth: "80%", p: 1.5, borderRadius: 2, backgroundColor: msg.role === "user" ? default_theme.palette.other.figmaBlue : "white", color: msg.role === "user" ? "white" : "text.primary", boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{msg.content}</Typography>
                    {msg.role !== "user" && msg.sources && msg.sources.length > 0 && (
                      <Box sx={{ mt: 1, pt: 1, display: "flex", flexWrap: "wrap", gap: 0.8, borderTop: "1px solid #f1f5f9" }}>
                        {msg.sources.map((src, srcIdx) => (
                          <Tooltip
                            key={`${src.id || srcIdx}-${srcIdx}`}
                            title={src.snippet || "No preview available for this source."}
                            placement="top"
                            arrow
                          >
                            <Chip
                              label={src.title || `Source ${srcIdx + 1}`}
                              size="small"
                              sx={{ backgroundColor: "#eef6ff", color: default_theme.palette.other.figmaBlue, fontWeight: 600, fontSize: "0.7rem" }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))
            )}
            {loading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, color: "text.secondary" }}>
                <CircularProgress size={16} />
                <Typography variant="caption">{loadingText}</Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          <Box sx={{ p: 2, backgroundColor: "white", display: "flex", alignItems: "flex-end", gap: 1 }}>
            <TextField
              fullWidth size="small" multiline maxRows={3} placeholder="Ask about the documents..."
              value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress}
            />
            <IconButton color="primary" disabled={!input.trim() || loading} onClick={handleSend} sx={{ backgroundColor: default_theme.palette.other.figmaBlue, borderRadius: 2 }}>
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default PDFChatPopup;